/**
 * ChatBot - Sự kiện (event-based chatbot settings)
 *
 * Two views:
 *   - "bots" (default): 2-pane — BotListSidebar (380px) + BotDetailPanel.
 *   - "logs": fullwidth AllLogsPage listing logs across the whole account.
 *
 * URL state: ?view=bots|logs · ?botId=<id|"new"> · tab=<overview|messages|config>
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Bot as BotIcon, ScrollText } from "lucide-react";
import { cn } from "../../../../utils/utils";
import chatbotApi from "../../../../api/chatbotApi";
import shopApi from "../../../../api/shopApi";
import tagApi from "../../../../api/tagApi";
import BotListSidebar from "../../components/BotListSidebar";
import BotDetailPanel from "../../components/BotDetailPanel";
import AllLogsPage from "../../components/AllLogsPage";

const VALID_TABS = ["overview", "messages", "config"];
const VALID_VIEWS = ["bots", "logs"];

const ChatbotSettingsPage = ({ chatbotType = "event" }) => {
  const auth = useSelector((state) => state.auth);
  const accountId = auth?.user?.accountId;
  const staffId = auth?.user?.id;

  const [settings, setSettings] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [shopsList, setShopsList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [zaloTagsList, setZaloTagsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("botId") || null;
  const rawTab = searchParams.get("tab") || "overview";
  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : "overview";
  const rawView = searchParams.get("view") || "bots";
  const activeView = VALID_VIEWS.includes(rawView) ? rawView : "bots";

  const isNew = selectedId === "new";

  const setActiveView = useCallback(
    (view) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (view === "bots") next.delete("view");
          else next.set("view", view);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const selectedBot = useMemo(
    () => (isNew ? null : settings.find((s) => s.id === selectedId) || null),
    [settings, selectedId, isNew],
  );

  // Update URL helpers
  const setSelectedId = useCallback(
    (id) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("botId", id);
          else next.delete("botId");
          // Reset to overview when selecting a different bot
          next.set("tab", "overview");
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setActiveTab = useCallback(
    (tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Load all data
  const loadData = useCallback(async () => {
    if (!accountId || !staffId) return;
    try {
      setLoading(true);
      const [settingsRes, eventTypesRes, shopsRes, tagsRes, zaloTagsRes] =
        await Promise.all([
          chatbotApi.getSettings(accountId, chatbotType),
          chatbotApi.getEventTypes(accountId),
          shopApi.getShops({ accountId, staffId }),
          tagApi.getByAccountId(accountId),
          tagApi.getZaloTagsByAccountId(accountId),
        ]);

      if (settingsRes.status === 1) {
        const sorted = [...settingsRes.data].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setSettings(sorted);
      }
      if (eventTypesRes.status === 1) setEventTypes(eventTypesRes.data || []);
      if (Array.isArray(shopsRes)) setShopsList(shopsRes);
      if (tagsRes.status === 1) setTagsList(tagsRes.data || []);
      else if (Array.isArray(tagsRes)) setTagsList(tagsRes);
      if (zaloTagsRes.status === 1) setZaloTagsList(zaloTagsRes.data || []);
      else if (Array.isArray(zaloTagsRes)) setZaloTagsList(zaloTagsRes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accountId, staffId, chatbotType]);

  useEffect(() => {
    document.title =
      chatbotType === "keyword"
        ? "Cài đặt QA Bot"
        : "Cài đặt ChatBot - Sự kiện";
    loadData();
  }, [loadData, chatbotType]);

  // Auto-select first bot once settings load (if none selected & none is new)
  useEffect(() => {
    if (loading) return;
    if (selectedId) return;
    if (settings.length > 0) {
      setSelectedId(settings[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, settings.length]);

  // CRUD handlers
  const handleCreateNew = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("botId", "new");
        next.set("tab", "config");
        return next;
      },
      { replace: false },
    );
  };

  const handleSaveNew = async (data) => {
    const res = await chatbotApi.createSetting({
      ...data,
      accountId,
      chatbotType,
      staffCreateId: staffId,
    });
    if (res.status !== 1) {
      throw new Error(res.message || "Lỗi khi tạo bot");
    }
    setSettings((prev) => [res.data, ...prev]);
    // Switch to the newly created bot's overview
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("botId", res.data.id);
        next.set("tab", "overview");
        return next;
      },
      { replace: true },
    );
  };

  const handleCancelNew = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("botId");
        next.delete("tab");
        return next;
      },
      { replace: true },
    );
  };

  const handleUpdate = async (id, data, opts = {}) => {
    const res = await chatbotApi.updateSetting(id, {
      ...data,
      accountId,
      staffUpdateId: staffId,
    });
    if (res.status !== 1) {
      throw new Error(res.message || "Lỗi khi cập nhật");
    }
    setSettings((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, ...data, updatedAt: new Date().toISOString() }
          : s,
      ),
    );
    if (!opts.silent) {
      setActiveTab("overview");
    }
  };

  const handleDelete = async (id) => {
    const res = await chatbotApi.deleteSetting(id, accountId);
    if (res.status !== 1) {
      alert(res.message || "Lỗi khi xóa");
      return;
    }
    setSettings((prev) => prev.filter((s) => s.id !== id));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("botId");
        next.delete("tab");
        return next;
      },
      { replace: true },
    );
  };

  const handleDuplicate = (bot) => {
    // Switch to a "new" form pre-populated with this bot's fields
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("botId", "new");
        next.set("tab", "config");
        return next;
      },
      { replace: false },
    );
    // Optimistic: stash the seed in sessionStorage so the form picks it up?
    // For now, simpler: just open new form with defaults; user copies manually.
    // Better: pass `initial` directly via a ref. Simpler MVP: use sessionStorage.
    try {
      sessionStorage.setItem(
        "__chatbot_duplicate_seed__",
        JSON.stringify({ ...bot, id: undefined }),
      );
    } catch {
      /* ignore */
    }
  };

  // When entering "new" mode, check sessionStorage for duplicate seed
  const [duplicateSeed, setDuplicateSeed] = useState(null);
  useEffect(() => {
    if (!isNew) {
      setDuplicateSeed(null);
      return;
    }
    try {
      const raw = sessionStorage.getItem("__chatbot_duplicate_seed__");
      if (raw) {
        setDuplicateSeed(JSON.parse(raw));
        sessionStorage.removeItem("__chatbot_duplicate_seed__");
      }
    } catch {
      /* ignore */
    }
  }, [isNew]);

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-6 py-4 max-w-md">
          <div className="font-semibold mb-1">Không tải được dữ liệu</div>
          <div className="text-sm">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      <PageViewTabs activeView={activeView} onChange={setActiveView} />

      {activeView === "logs" ? (
        <AllLogsPage
          bots={settings}
          eventTypes={eventTypes}
          shopsList={shopsList}
          chatbotType={chatbotType}
        />
      ) : (
        <div className="flex-1 flex overflow-hidden">
          <BotListSidebar
            bots={settings}
            selectedId={isNew ? null : selectedId}
            onSelect={setSelectedId}
            onCreateNew={handleCreateNew}
            eventTypes={eventTypes}
            shopsList={shopsList}
            chatbotType={chatbotType}
            loading={loading}
          />
          <BotDetailPanel
            bot={selectedBot}
            isNew={isNew}
            hasBots={settings.length > 0}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            eventTypes={eventTypes}
            shopsList={shopsList}
            tagsList={tagsList}
            zaloTagsList={zaloTagsList}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onSaveNew={handleSaveNew}
            onCancelNew={handleCancelNew}
            onCreateNew={handleCreateNew}
            chatbotType={chatbotType}
            duplicateSeed={duplicateSeed}
          />
        </div>
      )}
    </div>
  );
};

const PAGE_VIEWS = [
  { value: "bots", label: "Theo bot", icon: BotIcon },
  { value: "logs", label: "Toàn bộ log", icon: ScrollText },
];

const PageViewTabs = ({ activeView, onChange }) => (
  <nav className="px-4 bg-white border-b border-gray-200 flex items-center gap-0 shrink-0">
    {PAGE_VIEWS.map((view) => {
      const ViewIcon = view.icon;
      const active = activeView === view.value;
      return (
        <button
          key={view.value}
          type="button"
          onClick={() => onChange(view.value)}
          className={cn(
            "relative px-4 py-3 text-sm font-medium transition-colors inline-flex items-center gap-2 -mb-px",
            active
              ? "text-indigo-600 border-b-2 border-indigo-500"
              : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent",
          )}
        >
          <ViewIcon size={15} />
          {view.label}
        </button>
      );
    })}
  </nav>
);

export default ChatbotSettingsPage;
